import { useState, memo, type MouseEvent } from "react";
import { Image, Badge } from "react-bootstrap";
import { BsPinAngle, BsPinAngleFill } from "react-icons/bs";
import { useTranslation } from "react-i18next";
import { getCountryFlagUrl } from "../../utils/countryFlag";
import { formatCityCountryLabel } from "../../utils/cityAsyncSelectOptions";
import { getEventObjectiveDisplayLabel } from "../../i18n/eventRegistrationI18n";
import {
  getAvatarUrl,
  getInitials,
  getMembershipBadgeKey,
  getConnectionMembershipDisplay,
  normalizeMembership,
} from "../../utils/connectionUserUtils";
import {
  isPaidPortalMember,
  isYouthOrEarlyStageMembership,
} from "../../utils/membershipTier";
import type { User } from "../../types/Types";
import EventAttendeeCardActions from "./EventAttendeeCardActions";
import {
  AVATAR_MOBILE_STYLE,
  AVATAR_STYLE,
  COL,
  GAP,
  colStyle,
  type EventAttendeeCardProps,
} from "./eventAttendeeCardShared";

export type {
  ConnectionState,
  AttendeeWithState,
  EventAttendeeCardProps,
} from "./eventAttendeeCardShared";

function getJobTitle(user: User): string {
  if (
    isPaidPortalMember(user) &&
    isYouthOrEarlyStageMembership(user.membershipName)
  ) {
    return user.studyInterest?.trim() || "";
  }
  return user.personalPositionCompany?.trim() || "";
}

function getCompanyName(user: User): string {
  if (
    isPaidPortalMember(user) &&
    isYouthOrEarlyStageMembership(user.membershipName)
  ) {
    return user.universityCollege?.trim() || "";
  }
  return user.businessName?.trim() || "";
}

function EventAttendeeCard({
  attendee,
  sequentialNumber,
  isCurrentUser,
  currentUserMembership,
  explorerLimitReached = false,
  isExplorerUser = false,
  onBlockedConnectionAction,
  onViewProfile,
  onSaveNote,
  onMessage,
  onConnect,
  onCancelRequest,
  onAccept,
  onIgnore,
  onRemoveConnection,
  sendConnectionId,
  cancellingId,
  hasNote = false,
  isSelected = false,
  onSelect,
}: EventAttendeeCardProps) {
  const { t } = useTranslation();
  const { user, actionUser, connectionState } = attendee;
  void onRemoveConnection;
  const [avatarError, setAvatarError] = useState(false);

  const avatarUrl = getAvatarUrl(user);
  const initials = getInitials(user);
  const jobTitle = getJobTitle(user);
  const companyName = getCompanyName(user);
  const sector = user.businessSectorIndustry?.trim() || "";
  // Name-only identity: membershipName is enough (empty / "Explorer" → Explorer).
  const membershipTarget = {
    membershipName: user.membershipName || actionUser.membershipName,
  };
  const membershipBadgeKey = getMembershipBadgeKey(membershipTarget);
  const membershipLabel = getConnectionMembershipDisplay(membershipTarget);
  const membershipNameFallback =
    membershipTarget.membershipName?.trim() || "";
  const membershipDisplay = membershipBadgeKey
    ? t(membershipBadgeKey)
    : membershipLabel ||
      normalizeMembership(membershipNameFallback) ||
      membershipNameFallback;
  const locationLabel = formatCityCountryLabel(user.city, user.country);
  const locationFlagUrl = getCountryFlagUrl(user.country, user.city);
  const showInitials = !avatarUrl || avatarError;
  const objectiveLabel = user.eventObjectives
    ? getEventObjectiveDisplayLabel(user.eventObjectives, t)
    : "";
  const displayName = `${user.firstName} ${user.lastName}`.trim();

  const actionProps = {
    user,
    actionUser,
    connectionState,
    isCurrentUser,
    currentUserMembership,
    explorerLimitReached,
    isExplorerUser,
    onBlockedConnectionAction,
    onViewProfile,
    onSaveNote,
    onMessage,
    onConnect,
    onCancelRequest,
    onAccept,
    onIgnore,
    sendConnectionId,
    cancellingId,
    hasNote,
  };

  const handlePinClick = (e: MouseEvent) => {
    e.stopPropagation();
    onSelect?.();
  };

  const pinButton = (className = "") => (
    <button
      type="button"
      className={`btn btn-sm rounded-circle p-0 d-inline-flex align-items-center justify-content-center border-0 ${
        isSelected ? "btn-primary" : "btn-light text-secondary"
      }${className ? ` ${className}` : ""}`}
      style={{ width: 24, height: 24 }}
      aria-pressed={isSelected}
      aria-label={
        isSelected
          ? t("events.attendeeUnpin", { name: displayName })
          : t("events.attendeePin", { name: displayName })
      }
      title={
        isSelected
          ? t("events.attendeePinnedShort")
          : t("events.attendeePinShort")
      }
      onClick={handlePinClick}
    >
      {isSelected ? (
        <BsPinAngleFill size={14} aria-hidden />
      ) : (
        <BsPinAngle size={14} aria-hidden />
      )}
    </button>
  );

  const renderAvatar = (mobile: boolean) => {
    const sizeStyle = mobile ? AVATAR_MOBILE_STYLE : AVATAR_STYLE;
    const size = mobile ? 56 : COL.avatar;
    return (
      <div
        className="position-relative flex-shrink-0"
        style={{ width: size, height: size }}
      >
        {isCurrentUser && (
          <Badge
            bg="secondary"
            className="position-absolute top-0 start-0 translate-middle"
            style={{ fontSize: 9, zIndex: 1 }}
          >
            {t("connections.you")}
          </Badge>
        )}
        {showInitials ? (
          <div
            className="d-flex align-items-center justify-content-center text-white fw-bold rounded"
            style={{
              ...sizeStyle,
              background: "linear-gradient(180deg, #031B3D 0%, #082F6F 100%)",
            }}
          >
            {initials}
          </div>
        ) : (
          <Image
            src={avatarUrl}
            alt={displayName}
            rounded
            loading="lazy"
            onError={() => setAvatarError(true)}
            style={sizeStyle}
          />
        )}
      </div>
    );
  };

  const locationBlock = locationLabel ? (
    <div className="d-inline-flex align-items-center gap-1 text-muted small">
      {locationFlagUrl && (
        <img
          src={locationFlagUrl}
          alt=""
          width={20}
          height={14}
          className="rounded flex-shrink-0"
        />
      )}
      <span>{locationLabel}</span>
    </div>
  ) : null;

  const textOrNbsp = (value: string) => (value ? value : "\u00A0");

  return (
    <div
      className={`px-3 py-3 rounded-3 border shadow-sm ${
        isSelected
          ? "bg-primary-subtle border-light"
          : "bg-white border-light"
      }`}
    >
      {/* Desktop: flexible horizontal row — content shrinks, actions stay visible */}
      <div
        className="d-none d-lg-flex align-items-center flex-nowrap w-100 min-w-0"
        style={{ gap: GAP }}
      >
        <div
          className="d-flex align-items-center flex-shrink-0"
          style={{ gap: 4, minWidth: COL.pin + COL.index }}
        >
          {pinButton()}
          <span
            className="text-muted small fw-semibold text-end"
            style={{ width: COL.index }}
            aria-label={`Attendee ${sequentialNumber}`}
          >
            {sequentialNumber}.
          </span>
        </div>

        {renderAvatar(false)}

        <div className="min-w-0" style={colStyle(COL.name, 1.1)}>
          <div className="d-flex align-items-center gap-2 min-w-0">
            <div className="fw-bold text-dark text-truncate">{displayName}</div>
          </div>
          <div className="text-muted small text-truncate" style={{ minHeight: 18 }}>
            {textOrNbsp(jobTitle)}
          </div>
        </div>

        <div
          className="d-flex align-items-center gap-1 text-muted small min-w-0"
          style={colStyle(COL.city)}
        >
          {locationLabel ? (
            <>
              {locationFlagUrl && (
                <img
                  src={locationFlagUrl}
                  alt=""
                  width={20}
                  height={14}
                  className="rounded flex-shrink-0"
                />
              )}
              <span className="text-truncate">{locationLabel}</span>
            </>
          ) : (
            <span>&nbsp;</span>
          )}
        </div>

        <div
          className="d-flex align-items-center text-muted small min-w-0"
          style={colStyle(COL.company)}
          title={companyName || undefined}
        >
          <span className="text-truncate">{textOrNbsp(companyName)}</span>
        </div>

        <div
          className="d-flex align-items-center text-muted small min-w-0"
          style={colStyle(COL.sector)}
          title={sector || undefined}
        >
          <span className="text-truncate">{textOrNbsp(sector)}</span>
        </div>

        <div
          className="d-flex align-items-center min-w-0"
          style={colStyle(COL.objective, 1.2)}
        >
          {objectiveLabel ? (
            <Badge
              bg="light"
              text="dark"
              pill
              className="fw-normal text-truncate mw-100"
              title={objectiveLabel}
            >
              {objectiveLabel}
            </Badge>
          ) : null}
        </div>

        <div
          className="d-flex align-items-center min-w-0"
          style={colStyle(COL.membership)}
        >
          {membershipDisplay ? (
            <Badge
              bg="light"
              text="dark"
              pill
              className="fw-bold text-uppercase text-truncate mw-100"
              style={{ fontSize: 10 }}
              title={membershipDisplay}
            >
              {membershipDisplay}
            </Badge>
          ) : null}
        </div>

        <EventAttendeeCardActions {...actionProps} mobile={false} />
      </div>

      {/* Mobile: profile card */}
      <div className="d-lg-none">
        <div className="d-flex align-items-start gap-3 mb-3">
          <div className="d-flex align-items-center gap-1 pt-1 flex-shrink-0">
            {pinButton()}
            <span className="text-muted small fw-semibold">
              {sequentialNumber}.
            </span>
          </div>
          {renderAvatar(true)}
          <div className="min-w-0 flex-grow-1">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="fw-bold text-dark">{displayName}</div>
            </div>
            {jobTitle ? <div className="text-muted small mb-1">{jobTitle}</div> : null}
            {companyName ? (
              <div className="text-muted small mb-1">{companyName}</div>
            ) : null}
            {sector ? <div className="text-muted small mb-1">{sector}</div> : null}
            {locationBlock}
          </div>
        </div>

        {(objectiveLabel || membershipDisplay) && (
          <div className="d-flex flex-wrap gap-2 mb-3">
            {objectiveLabel ? (
              <Badge bg="light" text="dark" pill className="fw-normal">
                {objectiveLabel}
              </Badge>
            ) : null}
            {membershipDisplay ? (
              <Badge
                bg="light"
                text="dark"
                pill
                className="fw-bold text-uppercase"
                style={{ fontSize: 10 }}
              >
                {membershipDisplay}
              </Badge>
            ) : null}
          </div>
        )}

        <EventAttendeeCardActions {...actionProps} mobile />
      </div>
    </div>
  );
}

export default memo(EventAttendeeCard);
